import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { definirAdminToken, limparAdminToken, selecionarConfiguracao } from '../../store/admin-auth-slice';
import type { RootState } from '../../store';
import { useAdminListarConfiguracoesQuery } from '../../api';
import { lerLojaExcluida } from '../../lib/lojas-admin';

/**
 * Entry-point do backoffice.
 *
 * Fluxo:
 *  1. Recebe `?token=` do Sischef, salva em localStorage.
 *  2. Lista as configurações da Unidade do admin (`GET /api/admin/configuracoes`).
 *  3. Se **vazia** → vai pra `/admin/criar-loja?primeira=1`.
 *     Se tem **uma** → seleciona automaticamente + `/admin/configuracao`.
 *     Se tem **várias** → vai pra `/admin/configuracao` (escolhe no Select).
 *
 * Essa verificação só ocorre **na entrada**. Depois disso o usuário navega
 * livremente e pode criar lojas adicionais via botão "+ Nova" no Drawer.
 */
export function AdminPage(): JSX.Element {
  const [params] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector((s: RootState) => s.adminAuth.token);
  const idConfigSelecionada = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada);

  useEffect(() => {
    const tokenUrl = params.get('token');
    if (tokenUrl) dispatch(definirAdminToken(tokenUrl));
  }, [dispatch, params]);

  const { data: configs, isLoading, isFetching, error } = useAdminListarConfiguracoesQuery(undefined, {
    skip: !token,
  });

  // Seleciona automaticamente quando há uma única config (na entrada).
  useEffect(() => {
    if (configs && configs.length === 1 && idConfigSelecionada == null) {
      dispatch(selecionarConfiguracao(configs[0]!.id));
    }
  }, [configs, idConfigSelecionada, dispatch]);

  useEffect(() => {
    if (configs && idConfigSelecionada != null && !configs.some((c) => c.id === idConfigSelecionada)) {
      dispatch(selecionarConfiguracao(configs[0]?.id ?? null));
    }
  }, [configs, idConfigSelecionada, dispatch]);

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ pt: 6 }}>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Sischef Cardápio
          </Typography>
          <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
            Acesso deve ser feito a partir do Sischef. Abra o backoffice pelo link
            gerado lá (que inclui o JWT em <code>?token=</code>).
          </Alert>
        </Paper>
      </Container>
    );
  }

  // Erro ANTES de loading — quando há erro, `configs` fica undefined e a tela
  // travaria no spinner se a ordem fosse invertida.
  if (error) {
    return <ErroEntrada erro={error} onTentarNovamente={() => dispatch(limparAdminToken())} />;
  }

  // Com outra requisição pendente o RTK adia a invalidação: não decide o destino com a excluída ainda ativa na lista.
  const excluida = lerLojaExcluida(location.state);
  const listaDesatualizada = !!excluida && !!configs?.some((c) => c.id === excluida.id && c.ativo);

  if (isLoading || isFetching || listaDesatualizada) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!configs) {
    // Defensivo — não deveria chegar aqui.
    return (
      <Container maxWidth="sm" sx={{ pt: 6 }}>
        <Paper variant="outlined" sx={{ p: 4 }}>
          <Alert severity="warning">Resposta vazia do servidor.</Alert>
        </Paper>
      </Container>
    );
  }

  // Sem lojas → vai criar a primeira.
  if (configs.length === 0) {
    return <Navigate to="/admin/criar-loja?primeira=1" replace state={location.state} />;
  }

  // Já tem lojas → entra no backoffice (a Configuração é a tela inicial; o
  // onboarding aparece como dialog + aviso enquanto estiver incompleto).
  return <Navigate to="/admin/configuracao" replace state={location.state} />;
}

interface ErroEntradaProps {
  erro: unknown;
  onTentarNovamente: () => void;
}

function ErroEntrada({ erro, onTentarNovamente }: ErroEntradaProps): JSX.Element {
  const status = (erro as { status?: number })?.status;
  const dados = (erro as { data?: { codigo?: string; mensagem?: string } })?.data;
  const codigo = dados?.codigo;
  const mensagem = dados?.mensagem;

  let titulo = 'Não foi possível carregar suas lojas';
  let detalhe: string;
  let acao = 'Acesse o backoffice novamente pelo Sischef';

  if (status === 401 || codigo === 'NAO_AUTENTICADO') {
    titulo = 'Sessão inválida';
    detalhe =
      mensagem ??
      'O token recebido do Sischef não foi aceito. Pode estar expirado, com chave divergente, ou o usuário pode estar desativado.';
  } else if (status === 403 || codigo === 'ADMIN_SEM_UNIDADE') {
    titulo = 'Usuário sem Unidade';
    detalhe =
      mensagem ??
      'Seu usuário Sischef não tem uma Unidade vinculada. Cadastre a Unidade no Sischef antes.';
    acao = 'Volte ao Sischef e configure a Unidade do usuário';
  } else if (typeof status === 'number') {
    detalhe = `HTTP ${status}${mensagem ? ` — ${mensagem}` : ''}`;
  } else {
    detalhe = 'Sem resposta do servidor. Verifique se o backend está rodando.';
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 6 }}>
      <Paper variant="outlined" sx={{ p: 4 }}>
        <Alert severity="error">
          <AlertTitle>{titulo}</AlertTitle>
          {detalhe}
        </Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {acao}
          </Typography>
          <Button variant="outlined" onClick={onTentarNovamente}>
            Limpar sessão
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
